import { Component, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';

import Swal from 'sweetalert2';
import CryptoJS from 'crypto-js';

import { REGEX } from '@shared/utils/regex.constants';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';
import { UserService } from '@core/http/table-data-services/User.service';
import { environment } from "@enviroment/environment";
import {decryptData} from "@shared/utils/decrypt";

@Component({
  selector: 'app-confirm-password',
  standalone: true,
  imports: [
    MatButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatSuffix,
    NgIf,
    ReactiveFormsModule,
    RouterLink
  ],
  templateUrl: './confirm-password.component.html',
  styleUrl: './confirm-password.component.scss'
})
export class ConfirmPasswordComponent implements OnInit {
  authForm!: UntypedFormGroup;

  hide = true;
  chide = true;

  /**
   * Este es el token cifrado ya decodificado desde la URL.
   * ESTE ES EL QUE SE ENVÍA AL BACKEND.
   */
  encryptedToken: string = '';

  passwordFormat = Validators.pattern(REGEX.password);

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private httpMsgHandler: HttpMesageHandlerService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadTokenAndEmail();
  }

  // =========================================================
  // FORM
  // =========================================================
  private buildForm(): void {
    this.authForm = this.formBuilder.group(
      {
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, this.passwordFormat]],
        confirmedPassword: ['', [Validators.required, this.passwordFormat]],
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  // =========================================================
  // GETTERS
  // =========================================================
  get emailCtrl() {
    return this.authForm.get('email');
  }

  get password() {
    return this.authForm.get('password');
  }

  get confirmedPassword() {
    return this.authForm.get('confirmedPassword');
  }

  get passwordsDoNotMatch() {
    return (
      this.authForm.errors?.['passwordsMismatch'] &&
      (this.password?.touched || this.confirmedPassword?.touched)
    );
  }

  // =========================================================
  // CARGA DE TOKEN Y EMAIL
  // =========================================================
  private loadTokenAndEmail(): void {
    this.route.params.subscribe((params) => {
      const rawRouteToken = (params['token'] ?? '').toString().trim();

      // 1) token real que vino en la URL
      this.encryptedToken = this.normalizeRouteToken(rawRouteToken);

      // 2) descifrar usando el helper real del proyecto
      const jwtToken = decryptData(this.encryptedToken, environment.encryptionKey);

      // 3) leer email del JWT
      const emailFromToken = this.tryGetEmailFromJwt(jwtToken);

      if (emailFromToken) {
        this.emailCtrl?.setValue(emailFromToken);
      }
    });
  }

  /**
   * Convierte %2B, %2F, %3D, etc. a su forma real.
   */
  private normalizeRouteToken(token: string): string {
    if (!token) return '';

    try {
      return decodeURIComponent(token).trim();
    } catch {
      return token.trim();
    }
  }

  // =========================================================
  // ACCIONES
  // =========================================================
  resetPassword(): void {
    if (!this.encryptedToken) {
      Swal.fire({
        icon: 'error',
        title: 'TOKEN INVÁLIDO',
        text: 'NO FUE POSIBLE LEER EL TOKEN DE CONFIRMACIÓN.'
      });
      return;
    }

    if (this.authForm.invalid || this.passwordsDoNotMatch) {
      this.authForm.markAllAsTouched();
      return;
    }

    const newPassword = (this.password?.value ?? '').toString().trim();
    const confirmPassword = (this.confirmedPassword?.value ?? '').toString().trim();

    this.userService.verifyEmail(this.encryptedToken, newPassword, confirmPassword).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: 'CUENTA CONFIRMADA',
          text: 'YA PUEDES INICIAR SESIÓN.'
        }).then(() => {
          this.router.navigate(['/authentication/signin']);
        });
      },
      error: (err) => this.httpMsgHandler.handleMsg(err),
    });
  }

  resendVerifyEmail(): void {
    const email = (this.emailCtrl?.value ?? '').toString().trim();

    if (!email) {
      this.emailCtrl?.markAsTouched();
      return;
    }

    this.userService.resendVerifyEmail(email).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: 'CORREO ENVIADO',
          text: 'SE REENVIÓ EL CORREO DE CONFIRMACIÓN.'
        });
      },
      error: (err) => this.httpMsgHandler.handleMsg(err),
    });
  }

  // =========================================================
  // VALIDADORES
  // =========================================================
  passwordsMatchValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const pw = group.get('password')?.value;
    const cpw = group.get('confirmedPassword')?.value;
    return pw === cpw ? null : { passwordsMismatch: true };
  };

  // =========================================================
  // JWT HELPERS
  // =========================================================
  private tryGetEmailFromJwt(token: string): string {
    try {
      if (!this.isJwt(token)) return '';

      const parts = token.split('.');
      const payload = parts[1];
      const json = this.base64UrlDecode(payload);
      const obj = JSON.parse(json);

      return (obj?.Email ?? obj?.email ?? '').toString().trim();
    } catch {
      return '';
    }
  }

  private isJwt(token: string): boolean {
    if (!token) return false;
    return token.split('.').length === 3;
  }

  private base64UrlDecode(input: string): string {
    let b64 = input.replace(/-/g, '+').replace(/_/g, '/');

    while (b64.length % 4) {
      b64 += '=';
    }

    return decodeURIComponent(
      Array.prototype.map
        .call(atob(b64), (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  }
}
